import {
  AgGrid,
  Button,
  ErrorMessage,
  Heading,
  IconDropdown,
  MaterialIcon,
  MobileResponsiveGrid,
  Spinner,
} from "@/common/components";
import {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useSelector,
} from "@/common/hooks";
import { PrivateContext } from "@/routes/PrivateRoute";
import {
  useGetManagementCompaniesQuery,
  useGetInvestmentVehiclesQuery,
} from "@/service/AdminService";
import { setInvestmentVehiclesData } from "@/store/adminSlice";

const InvestmentVehicles = () => {
  const {
    setNavHeader,
    skipToken,
    Routes,
    navigate,
    labels,
    setPageHeader,
    dispatch,
    setNavCancelFunc,
  } = useContext(PrivateContext);

  const [rowDataForCompany, setRowDataForCompany] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [vehiclesParam, setVehiclesParam] = useState(skipToken);
  const [rowDataVehicles, setRowDataVehicles] = useState([]);
  const [sortOptionVehicle, setSortOptionVehicle] = useState("Name (A to Z)");
  const [sortOptionCompany, setSortOptionCompany] = useState("Name (A to Z)");
  const storeData = useSelector(
    (state) => state?.adminReducer?.investmentVehicles
  );

  const checkStatus = useCallback((params) => {
    if (params?.value === true || params === true) {
      return (
        <MaterialIcon iconName="check_circle" iconClassName="check_circle" />
      );
    } else {
      return <MaterialIcon iconName="cancel" iconClassName="cancel" />;
    }
  }, []);

  const actionsDropdown = useCallback((props) => {
    return <IconDropdown {...props} list={props.list} />;
  }, []);

  const defaultColDef = {
    cellClass: "mt-1",
  };

  const columnDefsForCompany = [
    {
      headerName: labels?.investmentVehicle?.companyNameHeader,
      field: "name",
      sortable: true,
    },
    {
      headerName: labels?.investmentVehicle?.actions,
      colId: "actions",
      cellRenderer: actionsDropdown,
      cellRendererParams: {
        list: [
          {
            text: labels?.investmentVehicle?.details,
            path: Routes.managementCompanyDetails.path,
          },
        ],
      },
      sortable: false,
    },
    {
      headerName: labels?.investmentVehicle?.taxIdHeader,
      field: "taxId",
    },
  ];

  const columnDefsVehicle = [
    {
      headerName: labels?.investmentVehicle?.statusHeader,
      field: "isEnabled",
      cellRenderer: checkStatus,
      flex: 1,
    },
    {
      headerName: labels?.investmentVehicle?.vehicleNameHeader,
      field: "name",
      sortable: true,
    },
    {
      headerName: labels?.investmentVehicle?.actions,
      colId: "actions",
      cellRenderer: actionsDropdown,
      cellRendererParams: {
        list: [
          {
            text: labels?.investmentVehicle?.details,
            click: (e) => getDetailsForVehicle(e),
          },
        ],
      },
      sortable: false,
    },
    {
      headerName: labels?.investmentVehicle?.extVehicleHeader,
      field: "externalId",
    },
    {
      headerName: labels?.investmentVehicle?.tradingSymbolHeader,
      field: "tradingSymbol",
    },
    {
      headerName: labels?.investmentVehicle?.targetVehicleActivationDateHeader,
      field: "targetFundActivationDate",
    },
  ];

  const {
    data: managementCompanies,
    isFetching,
    error,
  } = useGetManagementCompaniesQuery();

  const {
    data: investmentVehicles,
    isFetching: isFetching1,
    error: error1,
  } = useGetInvestmentVehiclesQuery(vehiclesParam);

  const clearStateInStore = () => {
    dispatch(setInvestmentVehiclesData({}));
    navigate(-1);
    return;
  };

  const onCellClickedCompany = useCallback((e) => {
    if (e.colDef.colId !== "actions") {
      setCompanyId(e.data.id);
    }
  }, []);

  const onRowClickForMobileCompany = useCallback((row) => {
    // MobileResponsiveGrid should call this with the clicked row data
    setCompanyId(row?.id);
  }, []);

  const getRowClassCompany = useCallback(
    (params) => {
      if (params.data.id === companyId) {
        return "hover-bg";
      }
      return "";
    },
    [companyId]
  );

  const onFirstDataRendered = useCallback(
    (params) => {
      const findIdx = rowDataForCompany.findIndex((e) => e.id === companyId);
      if (findIdx >= 0) {
        params.api.ensureIndexVisible(findIdx, "top");
      }
    },
    [rowDataForCompany, companyId]
  );

  const addNewCompany = () => {
    navigate(Routes.managementCompanyDetails.path);
  };

  const getDetailsForVehicle = useCallback(
    (data) => {
      navigate(Routes.investmentVehicleDetails.path, {
        state: rowDataVehicles.find((e) => e.id === data?.id),
      });
    },
    [rowDataVehicles]
  );

  const addNewVehicle = () => {
    navigate(Routes.investmentVehicleDetails.path, {
      state: {
        imcId: companyId,
        isEnabled: true,
        investmentManagementCompany: {},
      },
    });
  };

  // memoize sorted arrays so identity stays stable (prevents mobile grid remount/reset)
  const sortedEntitiesCompanies = useMemo(() => {
    const arr = [...rowDataForCompany];
    arr.sort((a, b) => {
      if (sortOptionCompany === "Name (A to Z)") {
        return a?.name?.localeCompare(b?.name);
      } else if (sortOptionCompany === "Name (Z to A)") {
        return b?.name?.localeCompare(a?.name);
      }
      return 0;
    });
    return arr;
  }, [rowDataForCompany, sortOptionCompany]);

  const sortedEntitiesVehicle = useMemo(() => {
    const arr = [...rowDataVehicles];
    arr.sort((a, b) => {
      if (sortOptionVehicle === "Name (A to Z)") {
        return a?.name?.localeCompare(b?.name);
      } else if (sortOptionVehicle === "Name (Z to A)") {
        return b?.name?.localeCompare(a?.name);
      }
      return 0;
    });
    return arr;
  }, [rowDataVehicles, sortOptionVehicle]);

  useEffect(() => {
    if (
      managementCompanies &&
      managementCompanies?.investmentManagementCompanys
    ) {
      setRowDataForCompany(managementCompanies?.investmentManagementCompanys);
      if (!storeData?.companyId) {
        setCompanyId(managementCompanies?.investmentManagementCompanys[0].id);
      }
    }
  }, [managementCompanies, storeData?.companyId]);

  useEffect(() => {
    if (investmentVehicles && investmentVehicles?.investementVehicles) {
      setRowDataVehicles(investmentVehicles?.investementVehicles);
    }
  }, [investmentVehicles]);

  useEffect(() => {
    if (companyId) {
      dispatch(
        setInvestmentVehiclesData({
          companyId: companyId,
        })
      );
      setVehiclesParam(companyId);
    }
  }, [companyId]);

  useEffect(() => {
    setPageHeader(labels?.investmentVehicle?.title);
    setNavHeader(labels?.navbar?.admin);
    setNavCancelFunc(() => clearStateInStore);

    if (storeData?.companyId) {
      setCompanyId(storeData?.companyId);
    }
  }, []);

  return (
    <section className="container-x-large py-5">
      {(isFetching || isFetching1) && <Spinner />}
      <ErrorMessage error={error || error1} wrapperClassName="mb-2" />

      <Heading
        text={labels?.investmentVehicle?.managementCompanies}
        wrapperClassNames="h4 border-bottom-1 pb-2 mb-3"
        title={false}
      />
      <Button
        id="addCompany"
        text={labels?.investmentVehicle?.addCompany}
        wrapperClassName="col-md-3 col-lg-2 mb-2"
        click={addNewCompany}
      />
      <AgGrid
        printClassNames="mt-md-n3"
        columnDefs={columnDefsForCompany}
        rowData={rowDataForCompany}
        domLayout={"normal"}
        onCellClicked={onCellClickedCompany}
        getRowClass={getRowClassCompany}
        onFirstDataRendered={onFirstDataRendered}
        defaultColDef={defaultColDef}
      />
      <MobileResponsiveGrid
        rowData={sortedEntitiesCompanies}
        showSortDropDown={true}
        columnDefs={columnDefsForCompany}
        noDataLabel={labels?.investmentVehicle?.noVehicles}
        sortOption={sortOptionCompany}
        setSortOption={setSortOptionCompany}
        // new/important props:
        selectedRowId={companyId}
        onRowClick={onRowClickForMobileCompany}
        getRowClass={getRowClassCompany}
      />

      <Heading
        text={labels?.investmentVehicle?.investmentVehicles}
        wrapperClassNames="h4 border-bottom-1 pb-2 mb-3 mt-5"
        title={false}
      />

      <Button
        id="addVehicle"
        text={labels?.investmentVehicle?.addVehicle}
        wrapperClassName="col-md-3 col-lg-2 mb-2"
        click={addNewVehicle}
      />
      <AgGrid
        wrapperClassNames="mb-3 d-md-block d-none"
        printClassNames="mt-md-n3"
        columnDefs={columnDefsVehicle}
        rowData={rowDataVehicles}
        domLayout={"normal"}
        overlayNoRowsTemplate={labels?.investmentVehicle?.noVehicles}
        defaultColDef={defaultColDef}
      />
      <MobileResponsiveGrid
        rowData={sortedEntitiesVehicle}
        showSortDropDown={true}
        columnDefs={columnDefsVehicle}
        noDataLabel={labels?.investmentVehicle?.noVehicles}
        sortOption={sortOptionVehicle}
        setSortOption={setSortOptionVehicle}
      />
    </section>
  );
};

export default InvestmentVehicles;
